import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PhotoCard from "@/components/PhotoCard";
import QuickEditPanel from "@/components/QuickEditPanel";
import { Stack, UploadSimple } from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "needs_review", label: "In review" },
  { key: "approved", label: "Approved" },
  { key: "published", label: "Published" },
];

export default function MySubmissions() {
  const [photos, setPhotos] = useState(null);
  const [status, setStatus] = useState("all");
  const [editPhoto, setEditPhoto] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    const params = status !== "all" ? { status } : {};
    setPhotos(null);
    api.get("/me/photos", { params }).then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    await api.delete(`/photos/${id}`);
    setPhotos((ps) => ps.filter((p) => p.id !== id));
    toast.success("Removed");
  };
  const openEdit = (photo) => { setEditPhoto(photo); setEditOpen(true); };
  const onEdited = (id, updated) => setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...updated } : p)));

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#F27A54]">Your contributions</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">
            My Uploads {photos && <span className="text-lg font-semibold text-muted-foreground">({photos.length})</span>}
          </h1>
        </div>
        <Link to="/upload" data-testid="my-uploads-import"
          className="flex items-center gap-2 rounded-md bg-[#F27A54] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d9633f]">
          <UploadSimple size={16} weight="bold" /> Import photos
        </Link>
      </div>

      <div className="mt-4 inline-flex rounded-md border border-border bg-white p-1">
        {STATUSES.map((t) => (
          <button key={t.key} data-testid={`myfilter-${t.key}`} onClick={() => setStatus(t.key)}
            className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
              status === t.key ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-100"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {photos === null ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-md" />)}
        </div>
      ) : photos.length === 0 ? (
        <div className="mt-16 grid place-items-center text-center">
          <Stack size={48} className="text-zinc-300" weight="thin" />
          <p className="mt-4 font-display text-lg font-bold">Nothing here yet</p>
          <p className="text-sm text-muted-foreground">Photos you import will appear here for you to manage.</p>
          <Link to="/upload" className="mt-4 rounded-md bg-[#F27A54] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d9633f]">Import your first photo</Link>
        </div>
      ) : (
        <div data-testid="my-uploads-grid" className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {photos.map((p, i) => (
            <PhotoCard key={p.id} photo={p} index={i} onDelete={remove} onEdit={openEdit} />
          ))}
        </div>
      )}

      <QuickEditPanel photo={editPhoto} open={editOpen} onOpenChange={setEditOpen} onSaved={onEdited} />
    </div>
  );
}
